/**
 * Constrained simulated comments from authored local pools only.
 * Selection is deterministic from (seed, score axes) — no public input.
 */
import { createSeededRng, type ScoreBreakdown } from "@desk/core";
import type { CrowdReaction } from "./emotion-model.js";
import type { LevelComments } from "../content/levels/index.js";
import type { SimulatedComment } from "./types.js";

interface CommentPoolEntry {
  handle: string;
  body: string;
  tone: SimulatedComment["tone"];
  /** Prefer when axis is high (0–1000 milli scale for loss/drift/provenance). */
  when?: "highReach" | "highDrift" | "highContextLoss" | "highProvenance" | "neutral";
}

const COMMENT_POOL: readonly CommentPoolEntry[] = [
  { handle: "@looped", body: "okay but the hook hits", tone: "hype", when: "highReach" },
  { handle: "@scrollbait", body: "instant replay energy", tone: "hype", when: "highReach" },
  { handle: "@deskfan", body: "wait… what happened after?", tone: "curious", when: "highContextLoss" },
  { handle: "@framewatch", body: "felt like something was just outside the shot", tone: "curious", when: "highContextLoss" },
  { handle: "@captioncop", body: "caption is doing a lot of work here", tone: "skeptical", when: "highDrift" },
  { handle: "@slowread", body: "is that what the source actually said?", tone: "skeptical", when: "highDrift" },
  { handle: "@castcheck", body: "subject look different or is it just me", tone: "skeptical", when: "highProvenance" },
  { handle: "@recon", body: "feels reconstructed, still watches clean though", tone: "neutral", when: "highProvenance" },
  { handle: "@midshift", body: "solid cut. moving on.", tone: "neutral", when: "neutral" },
  { handle: "@deskmate", body: "would share to the group chat", tone: "hype", when: "neutral" },
  { handle: "@quiettake", body: "mood is carrying this one", tone: "neutral", when: "neutral" },
  { handle: "@ticktick", body: "pacing feels intentional", tone: "curious", when: "neutral" },
];

const HIGH_REACH = 1800;
const HIGH_AXIS = 250;

export function pickSimulatedComments(
  seed: number,
  score: ScoreBreakdown,
  count = 4,
): SimulatedComment[] {
  const rng = createSeededRng((seed ^ 0xc0ffee) | 0);
  const preferred = new Set<CommentPoolEntry["when"]>(["neutral"]);
  if (score.reach >= HIGH_REACH) preferred.add("highReach");
  if (score.claimDrift >= HIGH_AXIS) preferred.add("highDrift");
  if (score.contextLoss >= HIGH_AXIS) preferred.add("highContextLoss");
  if (score.provenanceRisk >= HIGH_AXIS) preferred.add("highProvenance");

  const ranked = COMMENT_POOL.map((entry, index) => {
    const match = entry.when && preferred.has(entry.when) ? 1000 : 200;
    const jitter = rng();
    return { entry, index, rank: match + jitter };
  }).sort((a, b) => b.rank - a.rank || a.index - b.index);

  const n = Math.max(1, Math.min(count, ranked.length));
  return ranked.slice(0, n).map((row, i) => ({
    id: `cmt-${seed}-${i}`,
    handle: row.entry.handle,
    body: row.entry.body,
    tone: row.entry.tone,
  }));
}

// ── Per-crowd reaction comments (Job 5b) ──────────────────────────────────────
//
// The published reel's TOP COMMENTS are DATA-DRIVEN: each comment is derived from
// how a specific crowd actually felt about THIS edit (its `CrowdReaction` — face +
// whether it's eating it up / warming up / not landing / in backlash). The served
// crowd speaks first (reactions arrive served-first via `crowdReactions`), then the
// bystander crowds. A crowd in backlash reads hostile; a crowd eating it up reads
// hyped. Presentation only; never feeds `@desk/core` scoring.

type ReactionBucket = "backlash" | "hyped" | "warming" | "cold" | "bystander";

interface BucketCopy {
  tone: SimulatedComment["tone"];
  bodies: readonly string[];
}

const REACTION_COPY: Record<ReactionBucket, BucketCopy> = {
  backlash: {
    tone: "skeptical",
    bodies: [
      "nope. this is a setup and we all see it.",
      "receipts don't lie. hard unfollow.",
      "this is misleading and you know it.",
    ],
  },
  hyped: {
    tone: "hype",
    bodies: [
      "okay THIS is exactly what we wanted",
      "obsessed. replaying immediately.",
      "you get us. instant share.",
    ],
  },
  warming: {
    tone: "curious",
    bodies: [
      "almost had us, nearly there",
      "ok this is starting to click",
    ],
  },
  cold: {
    tone: "neutral",
    bodies: [
      "eh. didn't really land for us.",
      "scrolled right past, sorry.",
    ],
  },
  bystander: {
    tone: "neutral",
    bodies: [
      "saw this on the feed. fine i guess.",
      "no notes. moving on.",
    ],
  },
};

/** Which reaction bucket a crowd falls into for its (fallback) comment. */
function bucketForReaction(r: CrowdReaction): ReactionBucket {
  if (r.inRed) return "backlash";
  if (r.status === "eating it up") return "hyped";
  if (r.status === "warming up") return "warming";
  if (r.status === "not landing yet") return "cold";
  return "bystander";
}

/**
 * Index into a crowd's authored 5-line pool (ordered hate→love), chosen by that
 * crowd's ACTUAL reaction to THIS edit:
 *   0 backlash/hostile · 1 cold · 2 lukewarm · 3 warming · 4 hyped
 * `fit` is the crowd's green-wants fill (served or bystander alike), so the same
 * mapping reads truthfully for every crowd. Presentation only; never scores.
 */
export function reactionCommentIndex(r: CrowdReaction): number {
  if (r.inRed) return 0;
  if (r.fit >= 0.5) return 4;
  if (r.fit >= 0.34) return 3;
  if (r.fit >= 0.15) return 2;
  return 1;
}

/** Small stable hash of a crowd id, so body selection is deterministic. */
function hashCrowdId(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return h;
}

/** A social-style handle for a crowd, derived from its label. */
function handleForCrowd(r: CrowdReaction): string {
  const slug = r.label.toLowerCase().replace(/[^a-z0-9]+/g, "");
  return `@${slug || r.id.replace(/[^a-z0-9]+/gi, "")}`;
}

/**
 * Build the TOP COMMENTS for a published reel from the per-crowd reactions
 * (served crowd first). Each comment is the LEVEL-AUTHORED line for that crowd
 * whose hate→love slot matches the crowd's ACTUAL reaction to this edit — so a
 * crowd eating it up reads hyped, a crowd in backlash reads hostile, and every
 * crowd speaks in its own authored voice (no two crowds share text). Falls back
 * to the built-in generic bucket copy only when a crowd has no authored pool (old
 * data). Deterministic; presentation only, never feeds `@desk/core` scoring.
 */
export function commentsFromReactions(
  seed: number,
  reactions: readonly CrowdReaction[],
  pools: LevelComments = {},
  count = reactions.length,
): SimulatedComment[] {
  const n = Math.max(1, Math.min(count, reactions.length));
  return reactions.slice(0, n).map((r, i) => {
    const pool = pools[r.id];
    if (pool && pool.length > 0) {
      const idx = Math.min(reactionCommentIndex(r), pool.length - 1);
      const line = pool[idx]!;
      return { id: `cmt-${seed}-${r.id}-${i}`, handle: line.handle, body: line.body, tone: line.tone };
    }
    // Fallback: generic bucket copy for a crowd without an authored pool.
    const copy = REACTION_COPY[bucketForReaction(r)];
    const pick = Math.abs((seed ^ hashCrowdId(r.id)) % copy.bodies.length);
    return {
      id: `cmt-${seed}-${r.id}-${i}`,
      handle: handleForCrowd(r),
      body: copy.bodies[pick]!,
      tone: copy.tone,
    };
  });
}
